import ExcelJS from 'exceljs';
import type { CertificateRequestFormData } from '../types/applicationForm';

export type SalesReportMatchResult = {
  /** Item nos on the form that appear in the sales report. */
  matched: string[];
  /** Item nos on the form not found in the sales report. */
  unmatched: string[];
  /** Distinct codes found in the report (capped). */
  reportCodesSample: string[];
  note?: string;
};

const ITEM_CODE_RE = /\b[A-Z0-9][A-Z0-9._\-]{2,40}\b/gi;

function normalizeCode(raw: string): string {
  return raw.trim().toUpperCase();
}

function collectCodesFromText(text: string, into: Set<string>): void {
  const matches = text.match(ITEM_CODE_RE) ?? [];
  for (const m of matches) {
    const code = normalizeCode(m);
    if (code.length >= 3) into.add(code);
  }
}

async function searchableTextFromFile(file: File): Promise<string> {
  const name = file.name.toLowerCase();
  const mime = (file.type || '').toLowerCase();

  if (name.endsWith('.pdf') || mime === 'application/pdf') {
    // Keep pdfjs out of the main bundle (same pattern as DEM PO extract).
    const { extractPdfPlainText } = await import('./certificateRequestSalesReportPdf');
    return extractPdfPlainText(file);
  }

  if (name.endsWith('.csv') || name.endsWith('.txt')) {
    return file.text();
  }

  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const parts: string[] = [];
    workbook.eachSheet((sheet) => {
      sheet.eachRow((row) => {
        row.eachCell((cell) => {
          const text = String(cell.text ?? cell.value ?? '').trim();
          if (text) parts.push(text);
        });
      });
    });
    return parts.join(' ');
  }

  return '';
}

/**
 * Compare certificate-request item lines against codes found in optional sales-report files.
 * Best-effort: PDF (typical), Excel, CSV/TXT. Failure does not block submit.
 */
export async function matchCertificateRequestItemsToSalesReports(
  form: CertificateRequestFormData,
  files: File[],
): Promise<SalesReportMatchResult> {
  const formItems = form.items
    .map((row) => normalizeCode(row.item_no))
    .filter(Boolean);
  const uniqueForm = [...new Set(formItems)];

  if (!files.length) {
    return { matched: [], unmatched: uniqueForm, reportCodesSample: [], note: 'No sales report uploaded.' };
  }

  const reportCodes = new Set<string>();
  let haystack = '';
  const parseNotes: string[] = [];

  for (const file of files) {
    try {
      const text = await searchableTextFromFile(file);
      if (!text.trim()) {
        parseNotes.push(
          `${file.name}: no readable text (scanned image PDFs cannot be matched automatically).`,
        );
        continue;
      }
      haystack += ` ${normalizeCode(text)}`;
      collectCodesFromText(text, reportCodes);
    } catch {
      parseNotes.push(`${file.name}: could not read file.`);
    }
  }

  const matched: string[] = [];
  const unmatched: string[] = [];
  for (const code of uniqueForm) {
    if (haystack.includes(code) || reportCodes.has(code)) matched.push(code);
    else unmatched.push(code);
  }

  return {
    matched,
    unmatched,
    reportCodesSample: [...reportCodes].slice(0, 40),
    note: parseNotes.length ? parseNotes.join(' ') : undefined,
  };
}
